import {
  Survey,
  QuestionData,
  Answer,
  SectionData,
  SurveyItem,
  QuestionOptions,
} from "../types/survey";
import {
  ApiQuestionData,
  ApiQuestionOptions,
  ApiSurveyItem,
  ApiSectionData,
  SurveyStatus,
  ApiAnswerData,
} from "../hooks/api/surveys";
import { isCustomOptionAnswer } from "../components/Common/Survey/QuestionEditor";

const isOtherAnswer = (title: string): boolean => {
  const lower = title.toLowerCase();
  return lower.includes("other");
};

const hasCustomOptionFlags = (options?: ApiQuestionOptions): boolean => {
  if (!options) return false;

  return !!(
    options.multiline === true &&
    typeof options.minLength === "number" &&
    options.minLength > 0 &&
    typeof options.maxLength === "number" &&
    options.maxLength > 0
  );
};

const createApiOptions = (q: QuestionData): ApiQuestionOptions => {
  const options: ApiQuestionOptions = {
    enabled: q.options?.enabled ?? false,
  };

  if (q.type === "text") {
    if (q.config?.multiline) options.multiline = true;
    if (q.config?.minLength !== undefined && q.config.minLength > 0)
      options.minLength = q.config.minLength;
    if (q.config?.maxLength !== undefined && q.config.maxLength > 0)
      options.maxLength = q.config.maxLength;
    return options;
  }

  if (q.type === "image") {
    const hasCustom = q.answers?.some((a) => isCustomOptionAnswer(a));

    options.enabled = !!hasCustom;

    if (hasCustom) {
      options.multiline = true;
      options.minLength = 3;
      options.maxLength = 250;
    }

    return options;
  }

  const hasOtherAnswer = q.answers?.some((a) => isOtherAnswer(a.title));
  if (
    hasOtherAnswer &&
    (q.type === "singleSelect" || q.type === "multiSelect")
  ) {
    options.multiline = true;
    options.minLength = 3;
    options.maxLength = 250;
    options.enabled = true;
  }

  return options;
};

const transformAnswer = (a: Answer): ApiAnswerData & { id: string } => ({
  id: a.id,
  title: a.title,
  sort: a.sort,
  options: {
    enabled: a.options?.enabled ?? false,
    multiline: a.options?.multiline,
    minLength: a.options?.minLength,
    maxLength: a.options?.maxLength,
  },
  logic: a.logic || [],
});

const transformQuestion = (
  q: QuestionData,
): ApiQuestionData & { id: string } => {
  const base: ApiQuestionData & { id: string } = {
    id: q.id,
    title: q.title,
    type: q.type || "",
    sort: q.sort,
    options: createApiOptions(q),
    logic: q.logic || [],
  };

  if (
    q.type === "toggle" ||
    q.type === "singleSelect" ||
    q.type === "multiSelect" ||
    q.type === "image"
  ) {
    base.answers = (q.answers || []).map(transformAnswer);
  }

  return base;
};

const transformSection = (s: SectionData): ApiSectionData & { id: string } => ({
  id: s.id,
  title: s.title,
  sort: s.sort ?? 0,
  questions: (s.questions || []).map(transformQuestion),
  isRepeatable: s.isRepeatable,
});

const transformItem = (
  item: SurveyItem,
  index: number,
): ApiSurveyItem & { id: string } => ({
  id: item.id,
  sort: item.sort ?? index,
  question: item.question ? transformQuestion(item.question) : null,
  section: item.section ? transformSection(item.section) : null,
});

export const transformSurveyToApi = (
  survey: Survey,
): {
  title: string;
  status: SurveyStatus;
  items: (ApiSurveyItem & { id: string })[];
} => ({
  title: survey.title,
  status: survey.status as SurveyStatus,
  items: survey.items.map(transformItem),
});

export const transformApiQuestionToApp = (
  apiQ: ApiQuestionData & { id: string },
): QuestionData => {
  const config: QuestionData["config"] = {};

  const apiOptions = apiQ.options as any;

  const hasCustomOption = hasCustomOptionFlags(apiQ.options);

  const options: QuestionOptions = {
    enabled: apiOptions?.enabled ?? false,
  };

  if (apiQ.type === "text") {
    config.multiline = apiQ.options?.multiline;
    config.minLength = apiQ.options?.minLength;
    config.maxLength = apiQ.options?.maxLength;
  } else if (apiQ.type === "image") {
    config.customOption = apiQ.options?.enabled;
  } else if (apiQ.type === "singleSelect" || apiQ.type === "multiSelect") {
    const hasOtherInAnswers = (apiQ.answers || []).some((a) =>
      isOtherAnswer(a.title),
    );
    options.customOption = hasCustomOption || hasOtherInAnswers;
  }

  const answers: Answer[] = (apiQ.answers || [])
    .filter((a): a is ApiAnswerData & { id: string } => !!a.id)
    .map((a) => ({
      id: a.id,
      title: a.title,
      sort: a.sort,
      options: {
        enabled: a.options?.enabled ?? false,
        multiline: a.options?.multiline,
        minLength: a.options?.minLength,
        maxLength: a.options?.maxLength,
      },
      logic: a.logic || [],
    }));

  if (options.customOption && !answers.some((a) => isOtherAnswer(a.title))) {
    answers.push({
      id: `other-${Date.now()}`,
      title: "Other (please specify)",
      sort: answers.length,
      options: { enabled: true, multiline: true, minLength: 3, maxLength: 250 },
      logic: [],
    });
  }

  return {
    id: apiQ.id,
    title: apiQ.title,
    type: apiQ.type as QuestionData["type"],
    sort: apiQ.sort,
    required: false,
    options,
    config,
    logic: apiQ.logic || [],
    answers,
    files: apiQ.files || ([] as any),
  };
};

// export const transformApiSurveyToApp = (apiSurvey: any): Survey => {
//   return {
//     id: apiSurvey.id,
//     title: apiSurvey.title,
//     status: apiSurvey.status,
//     jobs: apiSurvey.jobs,
//     createdAt: apiSurvey.createdAt,
//     updatedAt: apiSurvey.updatedAt,
//     user: apiSurvey.user,
//     items: (apiSurvey.items || []).map(
//       (apiItem: any, index: number): SurveyItem => ({
//         id: apiItem.id,
//         sort: apiItem.sort ?? index,
//         question: apiItem.question
//           ? transformApiQuestionToApp(apiItem.question)
//           : null,
//         section: apiItem.section
//           ? {
//               id: apiItem.section.id,
//               title: apiItem.section.title,
//               sort: apiItem.section.sort,
//               questions: (apiItem.section.questions || []).map(
//                 transformApiQuestionToApp,
//               ),
//             }
//           : null,
//       }),
//     ),
//   };
// };
export const transformApiSurveyToApp = (apiSurvey: any): Survey => {
  const items = Array.isArray(apiSurvey.items) ? apiSurvey.items : [];

  return {
    id: apiSurvey.id,
    title: apiSurvey.title,
    status: apiSurvey.status,
    jobs: apiSurvey.jobs,
    createdAt: apiSurvey.createdAt,
    updatedAt: apiSurvey.updatedAt,
    user: apiSurvey.user,
    items: items.map(
      (apiItem: any, index: number): SurveyItem => ({
        id: apiItem.id,
        sort: apiItem.sort ?? index,
        question: apiItem.question
          ? transformApiQuestionToApp(apiItem.question)
          : null,
        section: apiItem.section
          ? {
              id: apiItem.section.id,
              title: apiItem.section.title,
              sort: apiItem.section.sort,
              questions: (apiItem.section.questions || []).map(
                transformApiQuestionToApp,
              ),
              isRepeatable: apiItem.section.isRepeatable,
            }
          : null,
      }),
    ),
  };
};
